import { useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Crown, Loader2, Check, X, Archive, Radar, PieChart, Globe2, Anchor, Rocket, Coins, RefreshCw, Zap, ExternalLink, Trash2, Radio, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import TreasuryDoctrine from '../components/TreasuryDoctrine';
import AdmiralWalkthrough from '../components/AdmiralWalkthrough';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt$ = (n) => n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M` : `$${(n || 0).toLocaleString()}`;

export default function AdmiralCommand() {
  const { user, loading, authHeaders } = useAuth();
  const [tourOpen, setTourOpen] = useState(false);
  const [data, setData] = useState(null);
  const [brokers, setBrokers] = useState([]);
  const [lineage, setLineage] = useState(null);
  const [junior, setJunior] = useState(null);
  const [forge, setForge] = useState(null);
  const [forgeInputs, setForgeInputs] = useState({});
  const [tab, setTab] = useState('telemetry');
  const [updating, setUpdating] = useState(null);
  const [newPin, setNewPin] = useState('');

  const loadAll = async () => {
    try {
      const [g, b, j, f] = await Promise.all([
        axios.get(`${API}/admiral/grand-command`, { headers: authHeaders(), withCredentials: true }),
        axios.get(`${API}/brokers`, { headers: authHeaders(), withCredentials: true }),
        axios.get(`${API}/admiral/junior/overview`, { headers: authHeaders(), withCredentials: true }),
        axios.get(`${API}/admiral/signal-forge`, { headers: authHeaders(), withCredentials: true }),
      ]);
      setData(g.data);
      setBrokers(b.data);
      setJunior(j.data);
      setForge(f.data);
    } catch {
      // not admin
    }
  };

  useEffect(() => { if (user?.role === 'admin') loadAll(); /* eslint-disable-next-line */ }, [user]);

  if (loading) return <div className="px-6 md:px-24 py-32 text-center text-[#A3A3A3]">Verifying admiral seal…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') {
    return (
      <div className="px-6 md:px-12 lg:px-24 py-24 min-h-[70vh]">
        <Crown size={40} className="text-[#D4AF37] mb-8" strokeWidth={1} />
        <h1 className="text-4xl font-extralight mb-4">Admiral access required.</h1>
        <p className="text-[#A3A3A3] font-light">This deck is reserved for Mercy.</p>
      </div>
    );
  }
  if (!data) return <div className="px-6 md:px-24 py-32 text-center"><Loader2 size={24} className="text-[#D4AF37] animate-spin mx-auto" /></div>;

  const updateBroker = async (id, status) => {
    setUpdating(id);
    try {
      await axios.patch(`${API}/brokers/${id}/status`, { status }, { headers: authHeaders(), withCredentials: true });
      await loadAll();
      toast.success(`Broker ${status.toLowerCase()}.`);
    } catch { toast.error('Update failed.'); }
    setUpdating(null);
  };

  const resetJuniorPin = async (childId) => {
    if (!newPin || newPin.length < 4) { toast.error('PIN must be 4–6 digits.'); return; }
    try {
      await axios.post(`${API}/admiral/junior/reset-pin`, { child_id: childId, new_pin: newPin }, { headers: authHeaders(), withCredentials: true });
      setNewPin('');
      toast.success('Junior PIN rotated.');
    } catch { toast.error('Reset failed.'); }
  };

  const saveSignalKey = async (provider) => {
    const key = (forgeInputs[provider] || '').trim();
    if (key.length < 4) { toast.error('Paste a valid API key.'); return; }
    setUpdating(`signal-${provider}`);
    try {
      await axios.post(`${API}/admiral/signal-forge`, { provider, api_key: key }, { headers: authHeaders(), withCredentials: true });
      setForgeInputs((p) => ({ ...p, [provider]: '' }));
      await loadAll();
      toast.success(`${provider.toUpperCase()} key vaulted. Signal updated.`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Vault write failed.'); }
    setUpdating(null);
  };

  const deleteSignalKey = async (provider) => {
    if (!window.confirm(`Remove ${provider} key from the vault?`)) return;
    setUpdating(`signal-${provider}`);
    try {
      await axios.delete(`${API}/admiral/signal-forge/${provider}`, { headers: authHeaders(), withCredentials: true });
      await loadAll();
      toast.success(`${provider.toUpperCase()} key removed.`);
    } catch { toast.error('Delete failed.'); }
    setUpdating(null);
  };

  const rev = data.revenue_telemetry;
  const port = data.portfolio;
  const net = data.broker_network;

  return (
    <div className="px-6 md:px-12 lg:px-24 py-16 min-h-[80vh]">
      {/* Hero */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12">
        <div className="md:col-span-8">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-6 flex items-center gap-3">
            <Crown size={14} strokeWidth={1.5} /> Grand Command · Admiral
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-extralight leading-[1] tracking-tight">
            All vessels.<br />
            <span className="font-display italic text-[#D4AF37]">One bridge.</span>
          </h1>
        </div>
        <div className="md:col-span-4 flex items-end justify-end">
          <div className="text-right">
            <button
              onClick={() => setTourOpen(true)}
              data-testid="admiral-take-tour"
              className="mb-4 text-[10px] tracking-[0.3em] uppercase border border-[#D4AF37] text-[#D4AF37] px-4 py-2 hover:bg-[#D4AF37] hover:text-[#050505] transition-colors flex items-center gap-2 ml-auto"
            >
              <HelpCircle size={11} strokeWidth={1.5} /> Take Tour
            </button>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-2">Lineage Doctrine</div>
            <div className="font-display text-3xl text-[#D4AF37]">{lineage?.doctrine_active || `${rev.admiral_pct} / ${rev.operations_pct} / ${rev.cyberkids_pct}`}</div>
            {lineage && (
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] mt-1">Tier {lineage.current_tier} · {lineage.current_tier_name}</div>
            )}
          </div>
        </div>
      </div>

      {/* Onboarding walkthrough (auto on first login + manually via Take Tour) */}
      <AdmiralWalkthrough forceOpen={tourOpen} onClose={() => setTourOpen(false)} />

      {/* Lineage Status Bar */}
      {lineage && (
        <div data-testid="admiral-lineage-bar" className="sov-border bg-[#0A0A0A] p-5 mb-12">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] flex items-center gap-2">
              <Crown size={11} strokeWidth={1.5} /> Path to Ancestral Sovereignty
            </div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">{lineage.path_to_ancestral}</div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {lineage.tiers.map((t) => (
              <div key={t.tier} className={`px-3 py-2 border ${t.active ? 'border-[#D4AF37] bg-[#D4AF37]/5' : 'border-[#2a2a2a] opacity-50'}`}>
                <div className="text-[9px] tracking-[0.3em] uppercase text-[#A3A3A3]">Tier {t.tier}</div>
                <div className={`text-sm font-light ${t.active ? 'text-[#D4AF37]' : 'text-[#A3A3A3]'}`}>{t.name} · {t.split}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Links to all command surfaces */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-12">
        <QuickLink to="/imperial-archives" icon={Archive} label="Imperial Archives" />
        <QuickLink to="/scouter" icon={Radar} label="Listing Scouter" />
        <QuickLink to="/fractional" icon={PieChart} label="Fractional Engine" />
        <QuickLink to="/currency-bridge" icon={Globe2} label="Currency Bridge" />
        <QuickLink to="/junior" icon={Anchor} label="Junior Command" />
      </div>

      {/* Tabs */}
      <div className="flex gap-8 border-t border-b border-[#D4AF37]/15 py-5 mb-12">
        {[
          { k: 'telemetry', l: 'Revenue Telemetry' },
          { k: 'portfolio', l: 'Portfolio' },
          { k: 'brokers', l: `Broker Roster (${net.total_applications})` },
          { k: 'signal', l: 'Signal Forge' },
          { k: 'junior', l: 'Junior Oversight' },
          { k: 'activity', l: 'Activity' },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`admiral-tab-${t.k}`}
            className={`text-[11px] tracking-[0.3em] uppercase transition-colors ${tab === t.k ? 'text-[#D4AF37]' : 'text-[#A3A3A3] hover:text-[#F5F5F5]'}`}
          >
            {t.l}
          </button>
        ))}
      </div>

      {/* Revenue Telemetry */}
      {tab === 'telemetry' && (
        <div className="space-y-8" data-testid="admiral-telemetry">
          {/* PRIVATE — Treasury Doctrine (50 / 40 / 10) · Mercy-only view */}
          <TreasuryDoctrine title="Treasury Doctrine · Private Command" />

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <BigStat label="Gross Revenue" value={fmt$(rev.gross_revenue_usd)} accent />
            <BigStat label="Admiral · Mercy" value={fmt$(rev.admiral_share_usd)} sublabel={`${rev.admiral_pct}%`} />
            <BigStat label="Operations" value={fmt$(rev.operations_share_usd)} sublabel={`${rev.operations_pct}%`} />
            <BigStat label="CyberKids" value={fmt$(rev.cyberkids_share_usd)} sublabel={`${rev.cyberkids_pct}%`} />
          </div>

          <div className="sov-border bg-[#0A0A0A] p-8">
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-6">Lineage Law Distribution</div>
            <div className="h-4 flex overflow-hidden border border-[#D4AF37]/30">
              <div className="bg-[#D4AF37]" style={{ width: `${rev.admiral_pct}%` }}></div>
              <div className="bg-[#AA8729]" style={{ width: `${rev.operations_pct}%` }}></div>
              <div className="bg-[#5A4B22]" style={{ width: `${rev.cyberkids_pct}%` }}></div>
            </div>
            <div className="flex justify-between mt-4 text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">
              <span>{rev.admiral_pct}% Admiral</span>
              <span>{rev.operations_pct}% Ops</span>
              <span>{rev.cyberkids_pct}% CyberKids</span>
            </div>
          </div>

          {Object.keys(rev.product_breakdown || {}).length > 0 && (
            <div className="sov-border bg-[#0A0A0A] p-8">
              <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-6">Product Breakdown</div>
              <div className="space-y-4">
                {Object.entries(rev.product_breakdown).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between border-b border-[#D4AF37]/15 pb-3">
                    <div>
                      <div className="text-sm font-light tracking-wide">{k}</div>
                      <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mt-1">{v.count} transactions</div>
                    </div>
                    <div className="font-display text-2xl text-[#D4AF37]">{fmt$(v.revenue_usd)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">
            {rev.transactions_count} settled transactions · {data.vessel_activity.boardings} boardings · {data.vessel_activity.flips_analyzed} flips
          </div>
        </div>
      )}

      {/* Portfolio */}
      {tab === 'portfolio' && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-testid="admiral-portfolio">
          <BigStat label="Total Properties" value={port.total_properties} />
          <BigStat label="AUM Curated" value={fmt$(port.total_aum_usd)} accent />
          <BigStat label="Nigeria Corridor" value={port.nigeria_listings} />
          <BigStat label="Pacific Corridor" value={port.usa_listings} />
        </div>
      )}

      {/* Brokers */}
      {tab === 'brokers' && (
        <div data-testid="admiral-brokers">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-10">
            <BigStat label="Target States" value={net.target_states} />
            <BigStat label="Applications" value={net.total_applications} />
            <BigStat label="Activated" value={net.activated} accent />
            <BigStat label="In Pipeline" value={net.in_pipeline} />
          </div>
          <div className="space-y-3">
            {brokers.length === 0 && (
              <div className="text-center text-[#A3A3A3] text-sm py-12 sov-border bg-[#0A0A0A]">
                No broker applications yet.
              </div>
            )}
            {brokers.map((b) => (
              <div key={b.id} className="sov-border bg-[#0A0A0A] p-6 flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-2">
                    <span className="text-sm font-medium tracking-wide">{b.full_name}</span>
                    <span className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">{b.state} · {b.years_experience}y</span>
                  </div>
                  <div className="text-xs text-[#A3A3A3] font-light">{b.email} · {b.phone}</div>
                  {b.specialties?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {b.specialties.map((s) => (
                        <span key={s} className="text-[9px] tracking-[0.2em] uppercase border border-[#D4AF37]/30 text-[#A3A3A3] px-2 py-1">{s}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-[10px] tracking-[0.3em] uppercase px-3 py-1.5 border ${
                    b.status === 'Activated' ? 'text-[#D4AF37] border-[#D4AF37]' :
                    b.status === 'Declined' ? 'text-[#666] border-[#444]' :
                    'text-[#A3A3A3] border-[#444]'
                  }`}>{b.status}</span>
                  {b.status !== 'Activated' && (
                    <button
                      onClick={() => updateBroker(b.id, 'Activated')}
                      disabled={updating === b.id}
                      data-testid={`broker-activate-${b.id}`}
                      className="p-2 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#050505] transition-colors"
                      title="Activate"
                    >
                      <Check size={14} strokeWidth={1.5} />
                    </button>
                  )}
                  {b.status !== 'Declined' && (
                    <button
                      onClick={() => updateBroker(b.id, 'Declined')}
                      disabled={updating === b.id}
                      data-testid={`broker-decline-${b.id}`}
                      className="p-2 border border-[#444] text-[#666] hover:border-[#888] hover:text-[#A3A3A3] transition-colors"
                      title="Decline"
                    >
                      <X size={14} strokeWidth={1.5} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Signal Forge — Live data provider intake */}
      {tab === 'signal' && (
        <div data-testid="admiral-signal-forge" className="space-y-8">
          {/* Status banner */}
          <div className="sov-border bg-gradient-to-br from-[#0A0A0A] via-[#0A0A0A] to-[#D4AF37]/5 p-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-2 flex items-center gap-2">
                  <Zap size={12} strokeWidth={1.5} /> Signal Forge · Live Data Intake Bridge
                </div>
                <p className="text-xs text-[#A3A3A3] font-light max-w-2xl">
                  Vault provider API keys to flip the AI Valuation Shield from <span className="text-[#A3A3A3]">FOUNDRY</span> to <span className="text-[#D4AF37]">LIVE SIGNAL</span>. Keys are stored server-side and never returned to the UI in plaintext.
                </p>
              </div>
              <div className="flex gap-4 text-right">
                <div>
                  <div className="text-[9px] tracking-[0.3em] uppercase text-[#A3A3A3]">USA Signal</div>
                  <div data-testid="signal-status-usa" className={`text-sm font-display tracking-wide mt-1 ${forge?.summary?.usa_signal?.status === 'LIVE' ? 'text-[#D4AF37]' : 'text-[#A3A3A3]'}`}>
                    {forge?.summary?.usa_signal?.status === 'LIVE' ? '● LIVE' : '○ FOUNDRY'}
                  </div>
                </div>
                <div>
                  <div className="text-[9px] tracking-[0.3em] uppercase text-[#A3A3A3]">Nigeria Signal</div>
                  <div data-testid="signal-status-nigeria" className={`text-sm font-display tracking-wide mt-1 ${forge?.summary?.nigeria_signal?.status === 'LIVE' ? 'text-[#D4AF37]' : 'text-[#A3A3A3]'}`}>
                    {forge?.summary?.nigeria_signal?.status === 'LIVE' ? '● LIVE' : '○ FOUNDRY'}
                  </div>
                </div>
                <div>
                  <div className="text-[9px] tracking-[0.3em] uppercase text-[#A3A3A3]">Vaulted</div>
                  <div className="text-sm font-display tracking-wide mt-1 text-[#F5F5F5]">{forge?.summary?.configured || 0} / {forge?.summary?.total_providers || 4}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Provider intake slots */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {(forge?.providers || []).map((p) => (
              <div key={p.provider} data-testid={`signal-card-${p.provider}`} className="sov-border bg-[#0A0A0A] p-6 flex flex-col">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Radio size={12} strokeWidth={1.5} className={p.configured ? 'text-[#D4AF37]' : 'text-[#666]'} />
                      <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">{p.region}</div>
                    </div>
                    <div className="font-display text-2xl text-[#F5F5F5]">{p.label}</div>
                    <p className="text-xs text-[#A3A3A3] font-light mt-1">{p.purpose}</p>
                  </div>
                  <span className={`text-[9px] tracking-[0.25em] uppercase px-2 py-1 border whitespace-nowrap ${p.configured ? 'border-[#D4AF37] text-[#D4AF37]' : 'border-[#444] text-[#666]'}`}>
                    {p.configured ? '● Vaulted' : '○ Open'}
                  </span>
                </div>

                <a
                  href={p.console_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`signal-console-${p.provider}`}
                  className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] hover:underline inline-flex items-center gap-1.5 mb-4"
                >
                  Open Provider Console <ExternalLink size={10} strokeWidth={1.5} />
                </a>

                {p.configured ? (
                  <div className="mb-4 p-3 border border-[#D4AF37]/20 bg-[#D4AF37]/5 font-mono text-xs text-[#D4AF37] tracking-wider break-all">
                    {p.masked_key}
                  </div>
                ) : (
                  <div className="mb-4 p-3 border border-dashed border-[#444] text-[10px] tracking-[0.3em] uppercase text-[#666]">
                    No key vaulted
                  </div>
                )}

                <div className="flex gap-2 mt-auto">
                  <input
                    type="password"
                    placeholder={p.configured ? 'Paste new key to rotate…' : 'Paste API key…'}
                    value={forgeInputs[p.provider] || ''}
                    onChange={(e) => setForgeInputs((prev) => ({ ...prev, [p.provider]: e.target.value }))}
                    data-testid={`signal-input-${p.provider}`}
                    className="flex-1 bg-transparent border border-[#444] focus:border-[#D4AF37] outline-none px-3 py-2 text-sm font-mono tracking-wider"
                  />
                  <button
                    onClick={() => saveSignalKey(p.provider)}
                    disabled={updating === `signal-${p.provider}`}
                    data-testid={`signal-save-${p.provider}`}
                    className="border border-[#D4AF37] text-[#D4AF37] text-[10px] tracking-[0.3em] uppercase px-4 hover:bg-[#D4AF37] hover:text-[#050505] transition-colors disabled:opacity-50 flex items-center gap-1"
                  >
                    {updating === `signal-${p.provider}` ? <Loader2 size={12} className="animate-spin" /> : <Zap size={11} strokeWidth={1.5} />}
                    Vault
                  </button>
                  {p.configured && (
                    <button
                      onClick={() => deleteSignalKey(p.provider)}
                      disabled={updating === `signal-${p.provider}`}
                      data-testid={`signal-delete-${p.provider}`}
                      className="border border-[#444] text-[#666] hover:border-[#888] hover:text-[#A3A3A3] px-3 transition-colors"
                      title="Remove key"
                    >
                      <Trash2 size={12} strokeWidth={1.5} />
                    </button>
                  )}
                </div>
                {p.updated_at && (
                  <div className="text-[9px] tracking-[0.25em] uppercase text-[#666] mt-3">
                    Last updated · {new Date(p.updated_at).toLocaleString()} by {p.updated_by || 'admin'}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Signal logic banner */}
          <div className="sov-border bg-[#0A0A0A] p-6">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-3">Signal Routing Doctrine</div>
            <ul className="text-xs text-[#A3A3A3] font-light space-y-2 leading-relaxed">
              <li><span className="text-[#D4AF37]">USA</span> properties flip to <span className="text-[#D4AF37]">LIVE</span> when <span className="text-[#F5F5F5]">Zillow</span> or <span className="text-[#F5F5F5]">ATTOM</span> is vaulted.</li>
              <li><span className="text-[#D4AF37]">Nigeria</span> properties flip to <span className="text-[#D4AF37]">LIVE</span> when <span className="text-[#F5F5F5]">Estate Intel</span> is vaulted.</li>
              <li><span className="text-[#D4AF37]">Google Maps</span> enriches both signals with geocoding + POI overlays.</li>
              <li>Without any key, the Shield runs on <span className="text-[#F5F5F5]">FOUNDRY</span> (GPT-4o reasoning) and is honestly labelled as such on every report.</li>
            </ul>
          </div>
        </div>
      )}

      {/* Junior Oversight */}
      {tab === 'junior' && (
        <div data-testid="admiral-junior" className="space-y-8">
          <div className="sov-border bg-[#0A0A0A] p-6">
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-2 flex items-center gap-2">
              <Anchor size={12} strokeWidth={1.5} /> Junior Admirals · Parental Oversight
            </div>
            <p className="text-xs text-[#A3A3A3] font-light max-w-2xl">
              {junior?.doctrine || 'Junior Admirals receive 5% family legacy + 2% live heir fund per vessel transaction.'}
            </p>
          </div>

          {!junior?.juniors?.length && (
            <div className="text-center text-[#A3A3A3] text-sm py-12 sov-border bg-[#0A0A0A]">No junior profiles yet.</div>
          )}

          {junior?.juniors?.map((j) => (
            <div key={j.id} data-testid={`admiral-junior-${j.id}`} className="sov-border bg-[#0A0A0A] p-8 space-y-6">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">Captain</div>
                  <div className="font-display text-3xl text-[#D4AF37]">{j.name}</div>
                  <div className="text-xs text-[#A3A3A3] mt-1">{j.rank} · Age {j.age} · {j.xp} XP</div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="New PIN"
                    value={newPin}
                    onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ''))}
                    data-testid={`junior-pin-input-${j.id}`}
                    className="w-28 bg-transparent border border-[#444] px-3 py-2 text-sm tracking-widest text-center focus:border-[#D4AF37] outline-none"
                  />
                  <button
                    onClick={() => resetJuniorPin(j.id)}
                    data-testid={`junior-pin-reset-${j.id}`}
                    className="border border-[#D4AF37] text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#050505] px-3 py-2 transition-colors flex items-center gap-2"
                  >
                    <RefreshCw size={11} strokeWidth={1.5} /> Rotate PIN
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <BigStat label="Long-Term (5%)" value={fmt$(j.stats.long_term_usd)} accent />
                <BigStat label="Live Adventure (2%)" value={fmt$(j.stats.liquid_usd)} />
                <BigStat label="Ideas Drafted" value={j.stats.ideas_total} />
                <BigStat label="Ships Launched" value={j.stats.ships_launched} accent />
              </div>

              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] mb-4 flex items-center gap-2">
                  <Rocket size={11} strokeWidth={1.5} /> Business Forge Activity
                </div>
                {j.business_forge.length === 0 && (
                  <div className="text-sm text-[#A3A3A3] font-light">No business ideas yet.</div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {j.business_forge.map((b) => (
                    <div key={b.id} className="border border-[#D4AF37]/15 p-4 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-light truncate">{b.product}</div>
                        {b.pitch && <div className="text-[10px] text-[#A3A3A3] italic truncate">{b.pitch}</div>}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="font-display text-lg text-[#D4AF37]">${b.price_usd?.toFixed(2)}</div>
                        <div className={`text-[9px] tracking-[0.25em] uppercase ${b.status === 'Launched' ? 'text-[#00E5FF]' : 'text-[#666]'}`}>{b.status}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Activity */}
      {tab === 'activity' && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-testid="admiral-activity">
          <BigStat label="Boardings" value={data.vessel_activity.boardings} />
          <BigStat label="Flips Analyzed" value={data.vessel_activity.flips_analyzed} />
          <BigStat label="Valuations" value={data.vessel_activity.valuations_run} />
          <BigStat label="Cadets Enrolled" value={data.cadet_academy.total_enrollments} />
        </div>
      )}
    </div>
  );
}

const BigStat = ({ label, value, sublabel, accent }) => (
  <div className="sov-border bg-[#0A0A0A] p-6">
    <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-3">{label}</div>
    <div className={`font-display text-3xl md:text-4xl ${accent ? 'text-[#D4AF37]' : 'text-[#F5F5F5]'}`}>{value}</div>
    {sublabel && <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] mt-2">{sublabel}</div>}
  </div>
);

const QuickLink = ({ to, icon: Icon, label }) => (
  <Link to={to} className="sov-border bg-[#0A0A0A] p-4 flex items-center gap-3 hover:border-[#D4AF37]/50 transition-colors group">
    <Icon size={16} className="text-[#D4AF37]" strokeWidth={1.5} />
    <span className="text-[11px] tracking-[0.25em] uppercase text-[#F5F5F5] group-hover:text-[#D4AF37] transition-colors">{label}</span>
  </Link>
);
